import { match } from 'ts-pattern'
import {
  autoGenerateNumericRanges,
  FieldValue,
  LabeledNumericRange,
  NumericRange,
} from './catalogUtils'
import { CSSProperties, useEffect, useState } from 'react'
import { Button, Popconfirm, Space } from 'antd'
import { produce } from 'immer'
import { DeleteOutlined, ThunderboltOutlined } from '@ant-design/icons'

const INPUT_STYLE: CSSProperties = {
  padding: '2px 4px',
  boxSizing: 'border-box',
  border: '1px solid #eee',
}

type NumericRangeEndpointControlProps = {
  type: 'Min' | 'Max'
  value: NumericRange
  setValue: (value: Partial<NumericRange>) => void
}
function NumericRangeEndpointControl(props: NumericRangeEndpointControlProps) {
  const rangeTypeLabel = match(props.type)
    .with('Min', () => ['[', '(', '−∞'] as const)
    .with('Max', () => [']', ')', '∞'] as const)
    .exhaustive()
  const [rangeTypeIdx, setRangeTypeIdx] = useState(
    match(props.type)
      .with('Min', () => {
        if (props.value.minInclusive !== undefined) return 0
        if (props.value.minExclusive !== undefined) return 1
        return 2
      })
      .with('Max', () => {
        if (props.value.maxInclusive !== undefined) return 0
        if (props.value.maxExclusive !== undefined) return 1
        return 2
      })
      .exhaustive(),
  )
  const [value, setValue] = useState(
    match(props.type)
      .with('Min', () => props.value.minInclusive ?? props.value.minExclusive)
      .with('Max', () => props.value.maxInclusive ?? props.value.maxExclusive)
      .exhaustive(),
  )
  useEffect(() => {
    switch (props.type) {
      case 'Min':
        switch (rangeTypeIdx) {
          case 0:
            return props.setValue({
              minInclusive: value,
              minExclusive: undefined,
            })
          case 1:
            return props.setValue({
              minInclusive: undefined,
              minExclusive: value,
            })
          case 2:
            return props.setValue({
              minInclusive: undefined,
              minExclusive: undefined,
            })
        }
        break
      case 'Max':
        switch (rangeTypeIdx) {
          case 0:
            return props.setValue({
              maxInclusive: value,
              maxExclusive: undefined,
            })
          case 1:
            return props.setValue({
              maxInclusive: undefined,
              maxExclusive: value,
            })
          case 2:
            return props.setValue({
              maxInclusive: undefined,
              maxExclusive: undefined,
            })
        }
        break
    }
    // note: including all the eslint-recommended deps will break this component, hence eslint disable below
  }, [rangeTypeIdx, value]) // eslint-disable-line react-hooks/exhaustive-deps
  const rangeTypeBtn = (
    <button
      key="range-type-btn"
      style={{
        width: '34px',
        height: '23px',
        boxSizing: 'border-box',
        margin: '0',
        padding: '2px 4px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#eee',
        border: 'none',
      }}
      onClick={() =>
        setRangeTypeIdx((rangeIdx) =>
          rangeIdx === rangeTypeLabel.length - 1 ? 0 : rangeIdx + 1,
        )
      }
    >
      {rangeTypeLabel[rangeTypeIdx]}
    </button>
  )
  const rangeInput = (
    <span style={{ width: '70px', height: '24px' }} key="range-input-container">
      {rangeTypeIdx === 2 ? undefined : (
        <input
          style={{
            width: '70px',
            height: '24px',
            ...INPUT_STYLE,
          }}
          type="number"
          value={value}
          onChange={(e) => setValue(parseFloat(e.target.value))}
        />
      )}
    </span>
  )
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        gap: '6px',
        minWidth: '90px',
      }}
    >
      {match(props.type)
        .with('Min', () => [rangeTypeBtn, rangeInput])
        .with('Max', () => [rangeInput, rangeTypeBtn])
        .exhaustive()}
    </div>
  )
}

type NumericFilterConfigEditorProps = {
  ranges: LabeledNumericRange[]
  setRanges: React.Dispatch<React.SetStateAction<LabeledNumericRange[]>>
  values: FieldValue[]
}
export function NumericFilterConfigEditor(
  props: NumericFilterConfigEditorProps,
) {
  const { ranges, setRanges, values } = props
  const autoCalcRanges = () => setRanges(autoGenerateNumericRanges(values))
  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <table style={{ width: '100%' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }}>Min</th>
            <th style={{ textAlign: 'left' }}>Max</th>
            <th style={{ textAlign: 'left' }}>Label</th>
          </tr>
        </thead>
        <tbody>
          {ranges.map((r, rIdx) => (
            <tr
              key={rIdx}
              style={{
                padding: '20px 0',
              }}
            >
              <td>
                <NumericRangeEndpointControl
                  type="Min"
                  value={ranges[rIdx]}
                  setValue={(value) =>
                    setRanges(
                      produce((ranges) => {
                        Object.assign(ranges[rIdx], value)
                      }),
                    )
                  }
                />
              </td>
              <td>
                <NumericRangeEndpointControl
                  type="Max"
                  value={ranges[rIdx]}
                  setValue={(value) =>
                    setRanges(
                      produce((ranges) => {
                        Object.assign(ranges[rIdx], value)
                      }),
                    )
                  }
                />
              </td>
              <td>
                <input
                  style={{
                    width: '180px',
                    height: '24px',
                    ...INPUT_STYLE,
                  }}
                  value={ranges[rIdx].label}
                  onChange={(e) =>
                    setRanges(
                      produce((ranges) => {
                        ranges[rIdx].label = e.target.value
                      }),
                    )
                  }
                />
              </td>
              <td>
                <button
                  style={{ width: '32px', height: '24px' }}
                  onClick={() =>
                    setRanges(
                      produce((ranges) => {
                        ranges.splice(rIdx, 1)
                      }),
                    )
                  }
                >
                  <DeleteOutlined />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: 'flex', flexDirection: 'row', gap: '12px' }}>
        <Button
          type="primary"
          onClick={() =>
            setRanges((ranges) => [
              ...ranges,
              {
                minInclusive: 0,
                maxInclusive: 0,
                label: '0 to 0',
              },
            ])
          }
        >
          Add Range
        </Button>
        <Popconfirm
          title="Auto-Calculate Ranges"
          description={
            <p>
              This will replace current ranges with auto-calculated ranges.
              <br />
              All customizations will be lost.
            </p>
          }
          onConfirm={autoCalcRanges}
          okButtonProps={{ danger: true, icon: <ThunderboltOutlined /> }}
          okText="Auto-Calculate"
        >
          <Button danger icon={<ThunderboltOutlined />}>
            Auto-Calculate
          </Button>
        </Popconfirm>
      </div>
    </Space>
  )
}
